/**
 * Per-tenant display settings for the Ediz mobile app (SchoolService —
 * `/SchoolMobileSettings`). One record per school: the identity/contact the app
 * shows, a welcome message and five section switches.
 *
 * The service only stores and serves this — nothing here filters any other API
 * response; the app is what honours each flag.
 *
 * PascalCase 1:1 with the wire format (the service serializes with
 * `PropertyNamingPolicy = null`), so no mappers. There is no id anywhere: the
 * record always belongs to the authenticated tenant.
 */

/** GET/PUT `/SchoolMobileSettings` response. */
export interface SchoolMobileSettings {
  /** The record's own id — echoed back on every save. */
  SchoolMobileSettingsId: string;
  /** Empty means "use the account name" — the fallback is the front's. */
  DisplaySchoolName: string | null;
  DisplayWebSite: string | null;
  DisplayEmail: string | null;
  DisplayTelephone: string | null;
  DisplayWhatsApp: string | null;
  UrlInstagram: string | null;
  UrlFacebook: string | null;
  UrlYouTube: string | null;
  OfficeHoursLine1: string | null;
  OfficeHoursLine2: string | null;
  OfficeHoursLine3: string | null;
  OfficeHoursLine4: string | null;
  WelcomeMessage: string | null;
  ShowTimetable: boolean;
  ShowSchoolCalendar: boolean;
  ShowAccessLog: boolean;
  /** Off by default: it exposes student names. */
  ShowClassmates: boolean;
  ShowTeachers: boolean;
  /** Read-only, comes from the account record. `null` = account has no square logo. */
  UrlLogo: string | null;
  AddedOnUtc: string;
  AddedBy: string;
  UpdatedOnUtc: string | null;
  UpdatedBy: string | null;
}

/** The subset the tenant can edit — everything else is stamped by the server. */
export type SchoolMobileSettingsEditable = Omit<
  SchoolMobileSettings,
  | 'SchoolMobileSettingsId'
  | 'UrlLogo'
  | 'AddedOnUtc'
  | 'AddedBy'
  | 'UpdatedOnUtc'
  | 'UpdatedBy'
>;

/**
 * What a form hands the store: the fields that form owns. The page edits the
 * section switches and the modal edits the identity/contact block, so neither
 * ever holds the whole record.
 */
export type SchoolMobileSettingsPatch = Partial<SchoolMobileSettingsEditable>;

/**
 * PUT `/SchoolMobileSettings` body — the whole editable record plus the id the
 * GET returned. The id is dropped only when the record has none yet.
 */
export type SchoolMobileSettingsRequest = SchoolMobileSettingsEditable & {
  SchoolMobileSettingsId?: string;
};

/**
 * The identity/contact block, edited in the modal. `OfficeHoursLine1..4` are
 * deliberately out: the app card shows the account's opening hours, like Trei.no.
 */
export type SchoolMobileSettingsDetails = Pick<
  SchoolMobileSettingsEditable,
  | 'DisplaySchoolName'
  | 'DisplayWebSite'
  | 'DisplayEmail'
  | 'DisplayTelephone'
  | 'DisplayWhatsApp'
  | 'UrlFacebook'
  | 'UrlInstagram'
  | 'UrlYouTube'
  | 'WelcomeMessage'
>;

/** The section switches, edited on the page. */
export type SchoolMobileSettingsSections = Pick<
  SchoolMobileSettingsEditable,
  'ShowTimetable' | 'ShowSchoolCalendar' | 'ShowAccessLog' | 'ShowClassmates' | 'ShowTeachers'
>;

/**
 * Merges one form's fields onto the loaded record: the PUT replaces the whole
 * thing, so a save that carried only its own half would wipe the other.
 */
export function buildSchoolMobileSettingsRequest(
  current: SchoolMobileSettings | null,
  changes: SchoolMobileSettingsPatch,
): SchoolMobileSettingsRequest {
  const request: SchoolMobileSettingsRequest = {
    DisplaySchoolName: current?.DisplaySchoolName ?? null,
    DisplayWebSite: current?.DisplayWebSite ?? null,
    DisplayEmail: current?.DisplayEmail ?? null,
    DisplayTelephone: current?.DisplayTelephone ?? null,
    DisplayWhatsApp: current?.DisplayWhatsApp ?? null,
    UrlInstagram: current?.UrlInstagram ?? null,
    UrlFacebook: current?.UrlFacebook ?? null,
    UrlYouTube: current?.UrlYouTube ?? null,
    OfficeHoursLine1: current?.OfficeHoursLine1 ?? null,
    OfficeHoursLine2: current?.OfficeHoursLine2 ?? null,
    OfficeHoursLine3: current?.OfficeHoursLine3 ?? null,
    OfficeHoursLine4: current?.OfficeHoursLine4 ?? null,
    WelcomeMessage: current?.WelcomeMessage ?? null,
    ShowTimetable: current?.ShowTimetable ?? true,
    ShowSchoolCalendar: current?.ShowSchoolCalendar ?? true,
    ShowAccessLog: current?.ShowAccessLog ?? true,
    ShowClassmates: current?.ShowClassmates ?? false,
    ShowTeachers: current?.ShowTeachers ?? true,
    ...changes,
  };

  if (current?.SchoolMobileSettingsId) {
    request.SchoolMobileSettingsId = current.SchoolMobileSettingsId;
  }

  return request;
}

/** Server-side caps — mirrored as `maxlength` on the inputs so 422 never fires. */
export const SCHOOL_MOBILE_SETTINGS_MAX_LENGTH = {
  DisplaySchoolName: 100,
  DisplayWebSite: 300,
  DisplayEmail: 300,
  DisplayTelephone: 20,
  DisplayWhatsApp: 20,
  UrlInstagram: 300,
  UrlFacebook: 300,
  UrlYouTube: 300,
  OfficeHoursLine: 100,
} as const;

export interface SchoolMobileSettingsState {
  /** Settings from the last successful load/save (null until loaded). */
  settings: SchoolMobileSettings | null;
}

export const initialSchoolMobileSettingsState: SchoolMobileSettingsState = {
  settings: null,
};
